import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, AlertTriangle, Clock, CheckCircle, XCircle,
  Settings, Plus, RefreshCw, Lock, Unlock, RotateCcw,
  CalendarDays, ChevronDown, ChevronUp, History, X, Save, ArrowLeft,
  Pencil, Zap,
} from "lucide-react";
import { AuthContext } from "../App";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS_FECHA = [
  { label: "1 mes",   meses: 1  },
  { label: "3 meses", meses: 3  },
  { label: "6 meses", meses: 6  },
  { label: "1 año",   meses: 12 },
  { label: "2 años",  meses: 24 },
];

const calcFechaPreset = (meses) => {
  const d = new Date();
  d.setMonth(d.getMonth() + meses);
  return d.toISOString().slice(0, 10);
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

const diasLabel = (n) => {
  if (n === null || n === undefined) return "—";
  if (n < 0) return `Vencida hace ${Math.abs(n)} día${Math.abs(n) !== 1 ? "s" : ""}`;
  if (n === 0) return "Vence hoy";
  return `${n} día${n !== 1 ? "s" : ""}`;
};

const ESTADO_CFG = {
  vencido:     { label: "Vencida",     bg: "bg-red-500/20",    text: "text-red-400",    border: "border-red-500/30" },
  bloqueado:   { label: "Bloqueada",   bg: "bg-red-900/30",    text: "text-red-300",    border: "border-red-700/50" },
  hoy:         { label: "Vence hoy",   bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  proximo:     { label: "Por vencer",  bg: "bg-yellow-500/20", text: "text-yellow-400", border: "border-yellow-500/30" },
  al_dia:      { label: "Al día",      bg: "bg-green-500/20",  text: "text-green-400",  border: "border-green-500/30" },
  sin_licencia:{ label: "Sin licencia",bg: "bg-slate-500/20",  text: "text-slate-400",  border: "border-slate-500/30" },
  libre:       { label: "Libre",       bg: "bg-sky-500/20",    text: "text-sky-300",    border: "border-sky-500/30" },
};

// Buscador de cliente (entidad) que paga la licencia
function ClientePicker({ clientes, value, label, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  const ql = q.trim().toLowerCase();
  const filtrados = (ql
    ? clientes.filter(c => `${c.nombre || ""} ${c.razon_social || ""} ${c.ruc || ""}`.toLowerCase().includes(ql))
    : clientes
  ).slice(0, 30);
  return (
    <div className="relative" ref={wrapRef}>
      {value ? (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-sm">
          <span className="text-white truncate">{label}</span>
          <button type="button" onClick={() => onSelect(null)} className="text-slate-400 hover:text-white ml-2"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <>
          <input
            type="text" value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Buscar cliente por nombre o RUC..."
            className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
          />
          {open && (
            <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg bg-arandu-dark border border-white/10 shadow-xl">
              {filtrados.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>}
              {filtrados.map(c => (
                <button type="button" key={c.id}
                  onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
                  className="block w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10">
                  {c.razon_social || c.nombre}{c.ruc ? <span className="text-xs text-slate-500"> · {c.ruc}</span> : null}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function estadoEfectivo(lic, diasAviso) {
  if (!lic) return "sin_licencia";
  if (lic.libre) return "libre";
  if (lic.bloqueada) return "bloqueado";
  const d = lic.dias_restantes;
  if (d === null || d === undefined || !lic.fecha_vencimiento) return "sin_licencia";
  if (d < 0) return "vencido";
  if (d === 0) return "hoy";
  if (d <= diasAviso) return "proximo";
  return "al_dia";
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function LicenciasPage() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdminRole = user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente";

  const [licencias, setLicencias] = useState([]);
  const [resumen, setResumen]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Modales
  const [modalNueva, setModalNueva]       = useState(false);
  const [modalConfig, setModalConfig]     = useState(false);
  const [modalHistorial, setModalHistorial] = useState(null); // licencia seleccionada
  const [detalleId, setDetalleId] = useState(null);            // licencia abierta en el modal de detalle
  const [detalleAvisos, setDetalleAvisos] = useState([]);
  const [modalExtender, setModalExtender] = useState(null);   // licencia seleccionada
  const [modalEditar, setModalEditar]     = useState(null);   // licencia seleccionada

  // Form nueva licencia
  const [formEmpresaId, setFormEmpresaId]     = useState("");
  const [formEmpresaNombre, setFormEmpresaNombre] = useState("");
  const [formLogoTipo, setFormLogoTipo]       = useState("");
  const [formFechaVenc, setFormFechaVenc]     = useState("");
  const [formNotas, setFormNotas]             = useState("");
  const [formPeriodo, setFormPeriodo]         = useState("mensual");
  const [formDescEsperada, setFormDescEsperada] = useState("");
  const [formLogoTipoEmisor, setFormLogoTipoEmisor] = useState("");
  const [formDiasAvisoPersonalizado, setFormDiasAvisoPersonalizado] = useState("");
  const [empresasList, setEmpresasList]       = useState([]);
  const [clientesList, setClientesList]       = useState([]);
  const [formCliente, setFormCliente]         = useState({ id: "", nombre: "", ruc: "" });
  const [formLibre, setFormLibre]             = useState(false);
  const [editCliente, setEditCliente]         = useState({ id: "", nombre: "", ruc: "" });
  const [editLibre, setEditLibre]             = useState(false);

  // Form editar licencia (auto-renovación + datos generales)
  const [editEmpresaNombre, setEditEmpresaNombre] = useState("");
  const [editLogoTipo, setEditLogoTipo]           = useState("");
  const [editPeriodo, setEditPeriodo]             = useState("mensual");
  const [editDescEsperada, setEditDescEsperada]   = useState("");
  const [editNotas, setEditNotas]                 = useState("");
  const [editLogoTipoEmisor, setEditLogoTipoEmisor] = useState("");
  const [editDiasAvisoPersonalizado, setEditDiasAvisoPersonalizado] = useState("");

  // Config
  const [configDias, setConfigDias]   = useState(30);
  const [configInput, setConfigInput] = useState("30");
  const [configDiasFacturar, setConfigDiasFacturar]   = useState(7);
  const [configFacturarInput, setConfigFacturarInput] = useState("7");

  // Ver factura vinculada a una entrada del historial
  const [modalFacturaVista, setModalFacturaVista] = useState(null); // { loading, error, factura }

  // Extender días
  const [extenderDias, setExtenderDias] = useState(2);

  // Busqueda / filtro
  const [busq, setBusq]   = useState("");
  const [filtro, setFiltro] = useState("todos"); // todos|vencido|bloqueado|hoy|proximo|al_dia

  const headers = { Authorization: `Bearer ${token}` };

  // ── Cargar datos ────────────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resLic, resConf] = await Promise.all([
        fetch(`${API}/admin/licencias`, { headers }),
        fetch(`${API}/admin/licencias/config`, { headers }),
      ]);
      if (!resLic.ok) throw new Error("Error al cargar licencias");
      const dataLic  = await resLic.json();
      const dataConf = resConf.ok ? await resConf.json() : { dias_aviso_banner: 30, dias_aviso_facturar: 7 };
      setLicencias(dataLic.licencias || []);
      setResumen(dataLic.resumen || {});
      setConfigDias(dataConf.dias_aviso_banner || 30);
      setConfigInput(String(dataConf.dias_aviso_banner || 30));
      setConfigDiasFacturar(dataConf.dias_aviso_facturar || 7);
      setConfigFacturarInput(String(dataConf.dias_aviso_facturar || 7));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!detalleId) { setDetalleAvisos([]); return; }
    fetch(`${API}/admin/licencias/${detalleId}/avisos`, { headers })
      .then(r => r.ok ? r.json() : { avisos: [] })
      .then(d => setDetalleAvisos(d.avisos || []))
      .catch(() => {});
  }, [detalleId, licencias]); // eslint-disable-line

  // Cargar empresas para los selectores (nueva licencia / empresa emisora)
  useEffect(() => {
    if (!modalNueva && !modalEditar) return;
    fetch(`${API}/admin/empresas-propias`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEmpresasList(Array.isArray(data) ? data : data.empresas || []))
      .catch(() => {});
  }, [modalNueva, modalEditar]); // eslint-disable-line

  // Clientes de la empresa EMISORA (la que factura la licencia)
  const emisorEfectivo = modalNueva
    ? (formLogoTipoEmisor || formLogoTipo)
    : modalEditar ? (editLogoTipoEmisor || editLogoTipo) : "";
  useEffect(() => {
    if (!modalNueva && !modalEditar) return;
    if (!emisorEfectivo) { setClientesList([]); return; }
    let alive = true;
    fetch(`${API}/admin/entidades?es_cliente=true&logo_tipo=${encodeURIComponent(emisorEfectivo)}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (alive) setClientesList(Array.isArray(data) ? data : data.entidades || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [modalNueva, modalEditar, emisorEfectivo]); // eslint-disable-line

  // ── Acciones ────────────────────────────────────────────────────────────────

  const accion = async (url, method = "POST", body = null) => {
    const opts = { method, headers: { ...headers, "Content-Type": "application/json" } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${API}${url}`, opts);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || "Error");
    }
    return r.json();
  };

  const handleBloquear = async (lic) => {
    if (!window.confirm(`¿Bloquear acceso a "${lic.empresa_nombre}"?`)) return;
    await accion(`/admin/licencias/${lic.id}/bloquear`);
    cargar();
  };

  const handleDesbloquear = async (lic) => {
    await accion(`/admin/licencias/${lic.id}/desbloquear`);
    cargar();
  };

  const handleResetearDias = async (lic) => {
    if (!window.confirm(`¿Resetear los días de licencia de "${lic.empresa_nombre}" a 0?\n\nLa fecha de vencimiento quedará en hoy y el acceso quedará bloqueado por vencimiento.`)) return;
    const hoy = new Date().toISOString().slice(0, 10);
    await accion(`/admin/licencias/${lic.id}`, "PUT", { fecha_vencimiento: hoy });
    cargar();
  };

  const handleExtender = async () => {
    if (!modalExtender) return;
    await accion(`/admin/licencias/${modalExtender.id}/extender`, "POST", { dias: extenderDias });
    setModalExtender(null);
    cargar();
  };

  const handleCrear = async () => {
    if (!formEmpresaId || !formFechaVenc) {
      alert("Empresa y fecha de vencimiento son requeridas");
      return;
    }
    await accion("/admin/licencias", "POST", {
      empresa_id: formEmpresaId,
      empresa_nombre: formEmpresaNombre,
      logo_tipo: formLogoTipo,
      fecha_vencimiento: formFechaVenc,
      notas: formNotas,
      periodo: formPeriodo,
      descripcion_esperada: formDescEsperada,
      logo_tipo_emisor: formLogoTipoEmisor,
      dias_aviso_facturar_personalizado: formDiasAvisoPersonalizado === "" ? null : Number(formDiasAvisoPersonalizado),
      cliente_id: formCliente.id, cliente_nombre: formCliente.nombre, cliente_ruc: formCliente.ruc,
      libre: formLibre,
    });
    setFormCliente({ id: "", nombre: "", ruc: "" }); setFormLibre(false);
    setModalNueva(false);
    setFormEmpresaId(""); setFormEmpresaNombre(""); setFormLogoTipo(""); setFormFechaVenc(""); setFormNotas("");
    setFormPeriodo("mensual"); setFormDescEsperada(""); setFormLogoTipoEmisor(""); setFormDiasAvisoPersonalizado("");
    cargar();
  };

  const handleAbrirEditar = (lic) => {
    setEditEmpresaNombre(lic.empresa_nombre || "");
    setEditLogoTipo(lic.logo_tipo || "");
    setEditPeriodo(lic.periodo === "anual" ? "anual" : "mensual");
    setEditDescEsperada(lic.descripcion_esperada || "");
    setEditNotas(lic.notas || "");
    setEditLogoTipoEmisor(lic.logo_tipo_emisor || "");
    setEditDiasAvisoPersonalizado(
      lic.dias_aviso_facturar_personalizado === null || lic.dias_aviso_facturar_personalizado === undefined
        ? "" : String(lic.dias_aviso_facturar_personalizado)
    );
    setEditCliente({ id: lic.cliente_id || "", nombre: lic.cliente_nombre || "", ruc: lic.cliente_ruc || "" });
    setEditLibre(!!lic.libre);
    setModalEditar(lic);
  };

  const handleGuardarEdicion = async () => {
    if (!modalEditar) return;
    await accion(`/admin/licencias/${modalEditar.id}`, "PUT", {
      empresa_nombre: editEmpresaNombre,
      logo_tipo: editLogoTipo,
      periodo: editPeriodo,
      descripcion_esperada: editDescEsperada,
      notas: editNotas,
      logo_tipo_emisor: editLogoTipoEmisor,
      dias_aviso_facturar_personalizado: editDiasAvisoPersonalizado === "" ? null : Number(editDiasAvisoPersonalizado),
      cliente_id: editCliente.id, cliente_nombre: editCliente.nombre, cliente_ruc: editCliente.ruc,
      libre: editLibre,
    });
    setModalEditar(null);
    cargar();
  };

  const handleGuardarConfig = async () => {
    const d = parseInt(configInput, 10);
    if (isNaN(d) || d < 1 || d > 365) { alert("Ingresá un valor entre 1 y 365 para el aviso de vencimiento"); return; }
    const df = parseInt(configFacturarInput, 10);
    if (isNaN(df) || df < 1 || df > 365) { alert("Ingresá un valor entre 1 y 365 para el aviso de facturación"); return; }
    await accion("/admin/licencias/config", "PUT", { dias_aviso_banner: d, dias_aviso_facturar: df });
    setConfigDias(d);
    setConfigDiasFacturar(df);
    setModalConfig(false);
    cargar();
  };

  const handleVerFactura = async (facturaId) => {
    setModalFacturaVista({ loading: true, error: null, factura: null });
    try {
      const r = await fetch(`${API}/admin/facturas/${facturaId}`, { headers });
      if (!r.ok) throw new Error("No se pudo cargar la factura");
      const data = await r.json();
      setModalFacturaVista({ loading: false, error: null, factura: data });
    } catch (e) {
      setModalFacturaVista({ loading: false, error: e.message, factura: null });
    }
  };

  // ── Filtrado ────────────────────────────────────────────────────────────────

  const licsFiltradas = licencias.filter(lic => {
    const est = estadoEfectivo(lic, configDias);
    if (filtro !== "todos" && est !== filtro) return false;
    if (busq) {
      const q = busq.toLowerCase();
      if (!lic.empresa_nombre?.toLowerCase().includes(q) && !lic.logo_tipo?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── Semáforo cards ──────────────────────────────────────────────────────────

  const cards = [
    {
      key: "vencido",
      label: "Vencidas / Bloqueadas",
      sub: "Requieren atención",
      icon: AlertTriangle,
      color: "text-red-400",
      bg: "bg-red-500/10 border-red-500/20",
      count: licencias.filter(l => ["vencido","bloqueado"].includes(estadoEfectivo(l, configDias))).length,
    },
    {
      key: "hoy",
      label: "Vencen hoy",
      sub: new Date().toLocaleDateString("es-PY"),
      icon: Clock,
      color: "text-orange-400",
      bg: "bg-orange-500/10 border-orange-500/20",
      count: licencias.filter(l => estadoEfectivo(l, configDias) === "hoy").length,
    },
    {
      key: "proximo",
      label: `Vencen en ${configDias} días`,
      sub: `Próximos ${configDias} días`,
      icon: CalendarDays,
      color: "text-yellow-400",
      bg: "bg-yellow-500/10 border-yellow-500/20",
      count: licencias.filter(l => estadoEfectivo(l, configDias) === "proximo").length,
    },
    {
      key: "al_dia",
      label: "Al día",
      sub: "Licencias activas",
      icon: CheckCircle,
      color: "text-green-400",
      bg: "bg-green-500/10 border-green-500/20",
      count: licencias.filter(l => estadoEfectivo(l, configDias) === "al_dia").length,
    },
  ];

  if (!isAdminRole) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-400">Sin permiso para ver esta sección.</p>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            title="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Shield className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Control de Licencias</h1>
            <p className="text-sm text-slate-400">Gestión de acceso por empresa cliente</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setModalConfig(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors"
          >
            <Settings className="w-4 h-4" /> Configurar semáforo
          </button>
          <button
            onClick={cargar}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setModalNueva(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Nueva licencia
          </button>
        </div>
      </div>

      {/* Cards semáforo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(card => (
          <button
            key={card.key}
            onClick={() => setFiltro(filtro === card.key ? "todos" : card.key)}
            className={`p-4 rounded-xl border text-left transition-all hover:scale-[1.02] ${card.bg} ${filtro === card.key ? "ring-2 ring-white/20" : ""}`}
          >
            <div className="flex items-start justify-between">
              <card.icon className={`w-6 h-6 ${card.color}`} />
              {loading && <RefreshCw className="w-3 h-3 text-slate-500 animate-spin" />}
            </div>
            <div className={`text-3xl font-bold mt-2 ${card.color}`}>{card.count}</div>
            <div className="text-sm font-medium mt-1">{card.label}</div>
            <div className="text-xs text-slate-400">{card.sub}</div>
          </button>
        ))}
      </div>

      {/* Barra de búsqueda y filtro activo */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={busq}
          onChange={e => setBusq(e.target.value)}
          placeholder="Buscar empresa..."
          className="px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500 w-64"
        />
        {filtro !== "todos" && (
          <button
            onClick={() => setFiltro("todos")}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm"
          >
            Filtro: {ESTADO_CFG[filtro]?.label} <X className="w-3 h-3 ml-1" />
          </button>
        )}
        <span className="text-sm text-slate-400 ml-auto">{licsFiltradas.length} empresa{licsFiltradas.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
      )}

      {/* Tabla */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/5 border-b border-white/10">
              <th className="px-4 py-3 text-left font-semibold text-slate-300">Empresa</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-300">Logo</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-300">Estado</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-300">Vencimiento</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-300">Días restantes</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-300">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {licsFiltradas.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  {loading ? "Cargando..." : "No hay licencias registradas"}
                </td>
              </tr>
            )}
            {licsFiltradas.map(lic => {
              const est = estadoEfectivo(lic, configDias);
              const cfg = ESTADO_CFG[est] || ESTADO_CFG.sin_licencia;
              return (
                <tr key={lic.id} onClick={() => setDetalleId(lic.id)} title="Click para ver el detalle" className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer">
                  {/* Empresa */}
                  <td className="px-4 py-3">
                    <span className="font-medium">{lic.empresa_nombre || "—"}</span>
                    {lic.descripcion_esperada && (() => {
                      const emisorEfectivo = lic.logo_tipo_emisor || lic.logo_tipo;
                      return (
                        <span title={`Auto-renovación activa: se extiende sola cuando una factura de "${emisorEfectivo}" con "${lic.descripcion_esperada}" en la descripción se marca como pagada (${lic.periodo === "anual" ? "+1 año" : "+1 mes"}).`}
                          className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 align-middle">
                          <Zap className="w-2.5 h-2.5" /> Auto-renovación
                          {lic.logo_tipo_emisor && lic.logo_tipo_emisor !== lic.logo_tipo && (
                            <span className="ml-1 opacity-80">(factura en {lic.logo_tipo_emisor})</span>
                          )}
                        </span>
                      );
                    })()}
                    {lic.libre && (
                      <span title="Empresa LIBRE: no se controla licencia (nunca se bloquea, no genera avisos ni se auto-renueva)."
                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-500/15 text-sky-300 border border-sky-500/25 align-middle">
                        Libre
                      </span>
                    )}
                    {lic.cliente_nombre && (
                      <p className="text-[11px] text-slate-400 mt-0.5">Paga: {lic.cliente_nombre}</p>
                    )}
                    {lic.descripcion_esperada && !lic.libre && !lic.cliente_id && !lic.cliente_ruc && (
                      <p className="text-[11px] text-amber-400 mt-0.5">Sin cliente asignado: no se auto-renueva</p>
                    )}
                    {lic.autoextension_usada && (
                      <span title="El cliente ya usó su autoextensión de 2 días para este vencimiento. Se vuelve a habilitar si extendés la licencia."
                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/15 text-violet-300 border border-violet-500/25 align-middle">
                        Autoextensión usada
                      </span>
                    )}
                    {lic.notas && <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[180px]">{lic.notas}</p>}
                  </td>
                  {/* Logo tipo */}
                  <td className="px-4 py-3 text-slate-400 text-xs">{lic.logo_tipo || "—"}</td>
                  {/* Estado */}
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                      {cfg.label}
                    </span>
                  </td>
                  {/* Vencimiento */}
                  <td className="px-4 py-3 text-center text-slate-300 whitespace-nowrap">
                    {fmtFecha(lic.fecha_vencimiento)}
                  </td>
                  {/* Días restantes */}
                  <td className={`px-4 py-3 text-center text-xs font-medium whitespace-nowrap ${cfg.text}`}>
                    {diasLabel(lic.dias_restantes)}
                  </td>
                  {/* Acciones */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      {/* Editar (empresa, período, descripción esperada, notas) */}
                      <button
                        onClick={() => handleAbrirEditar(lic)}
                        title="Editar licencia"
                        className="p-1.5 rounded-lg bg-slate-500/10 hover:bg-white/10 text-slate-400 hover:text-white border border-white/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {/* Extender */}
                      <button
                        onClick={() => { setModalExtender(lic); setExtenderDias(30); }}
                        title="Extender días"
                        className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 transition-colors"
                      >
                        <CalendarDays className="w-3.5 h-3.5" />
                      </button>
                      {/* Bloquear / Desbloquear */}
                      {est === "bloqueado" || lic.bloqueada ? (
                        <button
                          onClick={() => handleDesbloquear(lic)}
                          title="Desbloquear"
                          className="p-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 transition-colors"
                        >
                          <Unlock className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBloquear(lic)}
                          title="Bloquear"
                          className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-colors"
                        >
                          <Lock className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* Resetear días a 0 */}
                      <button
                        onClick={() => handleResetearDias(lic)}
                        title="Resetear días (vence hoy)"
                        className="p-1.5 rounded-lg bg-slate-500/10 hover:bg-orange-500/10 text-slate-400 hover:text-orange-400 border border-white/10 hover:border-orange-500/30 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      {/* Historial */}
                      <button
                        onClick={() => setModalHistorial(lic)}
                        title="Ver historial"
                        className="p-1.5 rounded-lg bg-slate-500/10 hover:bg-white/10 text-slate-400 hover:text-white border border-white/10 transition-colors"
                      >
                        <History className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Modal: Nueva licencia ── */}
      {modalNueva && (
        <Modal title="Nueva Licencia" onClose={() => setModalNueva(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Empresa (logo tipo)</label>
              <select
                value={formEmpresaId}
                onChange={e => {
                  const opt = empresasList.find(ep => ep.id === e.target.value);
                  setFormEmpresaId(e.target.value);
                  setFormEmpresaNombre(opt?.nombre || opt?.razon_social || "");
                  setFormLogoTipo(opt?.slug || "");
                  setFormCliente({ id: "", nombre: "", ruc: "" });
                }}
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Seleccionar empresa...</option>
                {empresasList.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.nombre || ep.razon_social} ({ep.slug})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Fecha de vencimiento</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {PRESETS_FECHA.map(p => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setFormFechaVenc(calcFechaPreset(p.meses))}
                    className={`px-2.5 py-1 rounded text-xs border transition-colors ${
                      formFechaVenc === calcFechaPreset(p.meses)
                        ? "bg-blue-600 border-blue-500 text-white"
                        : "bg-arandu-dark border-white/10 text-slate-300 hover:bg-white/5"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <DateInput
                value={formFechaVenc}
                onChange={e => setFormFechaVenc(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Notas (opcional)</label>
              <textarea
                value={formNotas}
                onChange={e => setFormNotas(e.target.value)}
                rows={2}
                placeholder="Plan contratado, observaciones..."
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-sky-300 cursor-pointer">
              <input type="checkbox" checked={formLibre} onChange={e => setFormLibre(e.target.checked)} />
              Empresa LIBRE (no se controla licencia: nunca bloquea, no avisa ni se renueva)
            </label>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Cliente que paga esta licencia</label>
              <ClientePicker
                clientes={clientesList}
                value={formCliente.id}
                label={formCliente.nombre}
                onSelect={c => setFormCliente(c ? { id: c.id, nombre: c.razon_social || c.nombre || "", ruc: c.ruc || "" } : { id: "", nombre: "", ruc: "" })}
              />
              <p className="text-[11px] text-slate-500 mt-1">La auto-renovación solo se dispara cuando la factura va a este cliente. Sin cliente, la licencia no se renueva sola.</p>
            </div>
            <div className="pt-2 border-t border-white/10">
              <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1 mb-2">
                <Zap className="w-3.5 h-3.5" /> Auto-renovación por venta confirmada (opcional)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Período</label>
                  <select
                    value={formPeriodo}
                    onChange={e => setFormPeriodo(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="mensual">Mensual (+1 mes)</option>
                    <option value="anual">Anual (+1 año)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Descripción esperada en la factura</label>
                  <input
                    type="text"
                    value={formDescEsperada}
                    onChange={e => setFormDescEsperada(e.target.value)}
                    placeholder='ej: "Licencia mensual"'
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Empresa emisora de la factura</label>
                  <select
                    value={formLogoTipoEmisor}
                    onChange={e => { setFormLogoTipoEmisor(e.target.value); setFormCliente({ id: "", nombre: "", ruc: "" }); }}
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Misma empresa de la licencia ({formLogoTipo || "—"})</option>
                    {empresasList.map(ep => (
                      <option key={ep.id} value={ep.slug}>{ep.nombre || ep.razon_social} ({ep.slug})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Aviso de facturación personalizado (días, opcional)</label>
                  <input
                    type="number"
                    min={1} max={365}
                    value={formDiasAvisoPersonalizado}
                    onChange={e => setFormDiasAvisoPersonalizado(e.target.value)}
                    placeholder="usa el default general"
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Si dejás la descripción vacía, esta licencia no se auto-renueva — seguís gestionándola a mano.
                Si la completás, cada vez que una factura de "{formLogoTipoEmisor || formLogoTipo || "esta empresa"}" que contenga ese texto se marque como pagada, el vencimiento se extiende solo.
                "Empresa emisora" es quién factura la licencia (puede ser distinta a la empresa cliente de arriba); si no la elegís, se busca en la facturación de la propia empresa cliente.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalNueva(false)} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors">Cancelar</button>
              <button onClick={handleCrear} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                <Save className="w-4 h-4 inline mr-1" /> Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Editar licencia ── */}
      {modalEditar && (
        <Modal title={`Editar licencia — ${modalEditar.empresa_nombre}`} onClose={() => setModalEditar(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nombre de empresa</label>
              <input
                type="text"
                value={editEmpresaNombre}
                onChange={e => setEditEmpresaNombre(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Logo tipo</label>
              <input
                type="text"
                value={editLogoTipo}
                onChange={e => { setEditLogoTipo(e.target.value); if (!editLogoTipoEmisor) setEditCliente({ id: "", nombre: "", ruc: "" }); }}
                placeholder="ej: arandujar"
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Notas</label>
              <textarea
                value={editNotas}
                onChange={e => setEditNotas(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-sky-300 cursor-pointer">
              <input type="checkbox" checked={editLibre} onChange={e => setEditLibre(e.target.checked)} />
              Empresa LIBRE (no se controla licencia: nunca bloquea, no avisa ni se renueva)
            </label>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Cliente que paga esta licencia</label>
              <ClientePicker
                clientes={clientesList}
                value={editCliente.id || editCliente.nombre}
                label={editCliente.nombre}
                onSelect={c => setEditCliente(c ? { id: c.id, nombre: c.razon_social || c.nombre || "", ruc: c.ruc || "" } : { id: "", nombre: "", ruc: "" })}
              />
              <p className="text-[11px] text-slate-500 mt-1">La auto-renovación solo se dispara cuando la factura va a este cliente. Sin cliente, la licencia no se renueva sola.</p>
            </div>
            <div className="pt-2 border-t border-white/10">
              <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1 mb-2">
                <Zap className="w-3.5 h-3.5" /> Auto-renovación por venta confirmada (opcional)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Período</label>
                  <select
                    value={editPeriodo}
                    onChange={e => setEditPeriodo(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="mensual">Mensual (+1 mes)</option>
                    <option value="anual">Anual (+1 año)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Descripción esperada en la factura</label>
                  <input
                    type="text"
                    value={editDescEsperada}
                    onChange={e => setEditDescEsperada(e.target.value)}
                    placeholder='ej: "Licencia mensual" (vacío = desactivada)'
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Empresa emisora de la factura</label>
                  <select
                    value={editLogoTipoEmisor}
                    onChange={e => { setEditLogoTipoEmisor(e.target.value); setEditCliente({ id: "", nombre: "", ruc: "" }); }}
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Misma empresa de la licencia ({editLogoTipo || "—"})</option>
                    {empresasList.map(ep => (
                      <option key={ep.id} value={ep.slug}>{ep.nombre || ep.razon_social} ({ep.slug})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Aviso de facturación personalizado (días, opcional)</label>
                  <input
                    type="number"
                    min={1} max={365}
                    value={editDiasAvisoPersonalizado}
                    onChange={e => setEditDiasAvisoPersonalizado(e.target.value)}
                    placeholder={`usa el default general (${configDiasFacturar}d)`}
                    className="w-full px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Se busca este texto (sin importar mayúsculas) en el concepto/ítems de las facturas de "{editLogoTipoEmisor || editLogoTipo || "esta empresa"}". Si coincide y la factura se marca como pagada, el vencimiento se extiende automáticamente.
                El aviso de facturación personalizado define a cuántos días del vencimiento aparece el popup de "falta facturar" en Ventas — si lo dejás vacío, usa el default general configurado en "Configurar semáforo".
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalEditar(null)} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors">Cancelar</button>
              <button onClick={handleGuardarEdicion} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                <Save className="w-4 h-4 inline mr-1" /> Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Extender días ── */}
      {modalExtender && (
        <Modal title={`Extender licencia — ${modalExtender.empresa_nombre}`} onClose={() => setModalExtender(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Vencimiento actual: <span className="text-white font-medium">{fmtFecha(modalExtender.fecha_vencimiento)}</span>
            </p>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Días a extender</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1} max={3650}
                  value={extenderDias}
                  onChange={e => setExtenderDias(Number(e.target.value))}
                  className="w-28 px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                />
                <div className="flex gap-1">
                  {[2, 7, 15, 30, 90, 365].map(d => (
                    <button key={d} onClick={() => setExtenderDias(d)}
                      className={`px-2 py-1 rounded text-xs border transition-colors ${extenderDias === d ? "bg-blue-600 border-blue-500 text-white" : "bg-arandu-dark border-white/10 text-slate-300 hover:bg-white/5"}`}>
                      +{d}d
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalExtender(null)} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors">Cancelar</button>
              <button onClick={handleExtender} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                Confirmar extensión
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Configuración ── */}
      {modalConfig && (
        <Modal title="Configuración de Licencias" onClose={() => setModalConfig(false)}>
          <div className="space-y-5">
            <div>
              <p className="text-sm text-slate-400 mb-2">
                Define a cuántos días del vencimiento se activa el banner de aviso para los clientes (LicenciaGate).
              </p>
              <label className="block text-xs text-slate-400 mb-1">Días de aviso al cliente antes del vencimiento</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1} max={365}
                  value={configInput}
                  onChange={e => setConfigInput(e.target.value)}
                  className="w-24 px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                />
                <span className="text-sm text-slate-400">días</span>
              </div>
              <div className="flex gap-2 mt-2">
                {[7, 14, 20, 30, 60].map(d => (
                  <button key={d} onClick={() => setConfigInput(String(d))}
                    className={`px-3 py-1 rounded text-xs border transition-colors ${configInput === String(d) ? "bg-blue-600 border-blue-500 text-white" : "bg-arandu-dark border-white/10 text-slate-300 hover:bg-white/5"}`}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-white/10">
              <p className="text-sm text-slate-400 mb-2">
                Define a cuántos días del vencimiento aparece el popup interno de "falta facturar" al abrir Ventas
                en la empresa emisora correspondiente. Este es el <span className="text-slate-300">default general</span> —
                cada licencia puede tener su propio valor personalizado desde "Editar licencia".
              </p>
              <label className="block text-xs text-slate-400 mb-1">Días de aviso de facturación (default general)</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1} max={365}
                  value={configFacturarInput}
                  onChange={e => setConfigFacturarInput(e.target.value)}
                  className="w-24 px-3 py-2 rounded-lg bg-arandu-dark border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500"
                />
                <span className="text-sm text-slate-400">días</span>
              </div>
              <div className="flex gap-2 mt-2">
                {[3, 5, 7, 10, 15].map(d => (
                  <button key={d} onClick={() => setConfigFacturarInput(String(d))}
                    className={`px-3 py-1 rounded text-xs border transition-colors ${configFacturarInput === String(d) ? "bg-blue-600 border-blue-500 text-white" : "bg-arandu-dark border-white/10 text-slate-300 hover:bg-white/5"}`}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalConfig(false)} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-colors">Cancelar</button>
              <button onClick={handleGuardarConfig} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                <Save className="w-4 h-4 inline mr-1" /> Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Historial ── */}
      {/* ── Modal: Detalle de licencia ── */}
      {detalleId && (() => {
        const d = licencias.find(l => l.id === detalleId);
        if (!d) return null;
        const est = estadoEfectivo(d, configDias);
        const cfg = ESTADO_CFG[est] || ESTADO_CFG.sin_licencia;
        const emisor = d.logo_tipo_emisor || d.logo_tipo || "—";
        const Fila = ({ k, children }) => (
          <div className="flex justify-between gap-4 py-1.5 border-b border-white/5 text-sm">
            <span className="text-slate-400">{k}</span>
            <span className="text-white text-right">{children}</span>
          </div>
        );
        const cerrar = () => setDetalleId(null);
        const btn = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-slate-200 hover:bg-white/10 text-xs transition-colors";
        return (
          <Modal title={`Detalle — ${d.empresa_nombre || "Licencia"}`} onClose={cerrar} wide>
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>{cfg.label}</span>
                <span className={`text-sm font-medium ${cfg.text}`}>{d.libre ? "Sin control de licencia" : diasLabel(d.dias_restantes)}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                <button className={btn} onClick={() => { cerrar(); handleAbrirEditar(d); }}><Pencil className="w-3.5 h-3.5" /> Editar</button>
                <button className={btn} onClick={() => { cerrar(); setModalExtender(d); setExtenderDias(30); }}><CalendarDays className="w-3.5 h-3.5" /> Extender</button>
                {d.bloqueada
                  ? <button className={btn} onClick={async () => { await handleDesbloquear(d); }}><Unlock className="w-3.5 h-3.5" /> Desbloquear</button>
                  : <button className={btn} onClick={async () => { await handleBloquear(d); }}><Lock className="w-3.5 h-3.5" /> Bloquear</button>}
                <button className={btn} onClick={async () => { await handleResetearDias(d); }}><RotateCcw className="w-3.5 h-3.5" /> Resetear días</button>
                <button className={btn} onClick={() => { cerrar(); setModalHistorial(d); }}><History className="w-3.5 h-3.5" /> Historial completo</button>
              </div>

              <div className="grid md:grid-cols-2 gap-x-8">
                <div>
                  <p className="text-xs font-semibold text-slate-300 mb-1 mt-2">Licencia</p>
                  <Fila k="Empresa">{d.empresa_nombre || "—"}</Fila>
                  <Fila k="Logo tipo">{d.logo_tipo || "—"}</Fila>
                  <Fila k="Vencimiento">{fmtFecha(d.fecha_vencimiento)}</Fila>
                  <Fila k="Libre">{d.libre ? "Sí" : "No"}</Fila>
                  <Fila k="Bloqueo manual">{d.bloqueada ? "Sí" : "No"}</Fila>
                  <Fila k="Bloqueo de emergencia">{d.bloqueo_emergencia ? "Sí" : "No"}</Fila>
                  <Fila k="Autoextensión usada">{d.autoextension_usada ? "Sí" : "No"}</Fila>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-300 mb-1 mt-2">Facturación y renovación</p>
                  <Fila k="Cliente que paga">{d.cliente_nombre || <span className="text-amber-400">Sin asignar</span>}</Fila>
                  <Fila k="Empresa emisora">{emisor}</Fila>
                  <Fila k="Período">{d.periodo === "anual" ? "Anual (+1 año)" : "Mensual (+1 mes)"}</Fila>
                  <Fila k="Descripción esperada">{d.descripcion_esperada || "—"}</Fila>
                  <Fila k="Auto-renovación">{d.descripcion_esperada && !d.libre && (d.cliente_id || d.cliente_ruc) ? "Activa" : "Inactiva"}</Fila>
                  <Fila k="Aviso de facturación">{d.dias_aviso_facturar_personalizado != null ? `${d.dias_aviso_facturar_personalizado} días` : `General (${configDiasFacturar} días)`}</Fila>
                </div>
              </div>

              {(() => {
                const sinCliente = !(d.cliente_id || d.cliente_ruc);
                const umbral = d.dias_aviso_facturar_personalizado != null ? d.dias_aviso_facturar_personalizado : configDiasFacturar;
                const probs = [];
                if (d.libre) probs.push("Está marcada como LIBRE: no avisa ni se renueva.");
                if (!d.descripcion_esperada) probs.push("Sin «Descripción esperada»: no avisa ni se renueva sola.");
                if (!d.libre && d.descripcion_esperada && sinCliente) probs.push("Sin cliente asignado: avisa, pero NO se renueva sola al pagar la factura.");
                if (!d.libre && d.descripcion_esperada && d.dias_restantes != null && d.dias_restantes > umbral) probs.push(`Todavía no entra en la ventana de aviso (avisa a ${umbral} días o menos del vencimiento).`);
                const ok = probs.length === 0;
                return (
                  <div className={`rounded-lg border px-3 py-2 text-xs ${ok ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-300" : "border-amber-500/25 bg-amber-500/5 text-amber-200"}`}>
                    <p className="font-semibold mb-1">Diagnóstico</p>
                    {ok ? (
                      <p>Todo configurado: avisa en Ventas de «{emisor}» y se renueva sola cuando se paga una factura a «{d.cliente_nombre}» que contenga «{d.descripcion_esperada}».</p>
                    ) : probs.map((p, i) => <p key={i}>• {p}</p>)}
                    {!d.libre && d.descripcion_esperada && (
                      <p className="mt-1 opacity-80">El aviso aparece solo al entrar a Ventas con la empresa «{emisor}» activa.</p>
                    )}
                  </div>
                );
              })()}

              {d.notas && (
                <div>
                  <p className="text-xs font-semibold text-slate-300 mb-1">Notas</p>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{d.notas}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-300 mb-1">Avisos de facturación ({detalleAvisos.length})</p>
                {detalleAvisos.length === 0 ? (
                  <p className="text-xs text-slate-500">Sin avisos registrados.</p>
                ) : (
                  <div className="max-h-40 overflow-auto space-y-1">
                    {detalleAvisos.map(a => (
                      <div key={a.id} className="flex items-center justify-between gap-3 text-xs px-3 py-2 rounded-lg bg-white/5">
                        <span className="text-slate-300">Aviso {fmtFecha((a.fecha_aviso || "").slice(0, 10))} · vence {fmtFecha(a.fecha_vencimiento)}</span>
                        {a.estado === "atendido"
                          ? <span className="text-emerald-300">Atendido {fmtFecha((a.atendido_at || "").slice(0, 10))}{a.factura_numero ? ` · Factura ${a.factura_numero}` : ""}</span>
                          : <span className="text-amber-300">Pendiente</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-300 mb-1">Últimos movimientos</p>
                {(d.historial || []).length === 0 ? (
                  <p className="text-xs text-slate-500">Sin movimientos.</p>
                ) : (
                  <div className="max-h-40 overflow-auto space-y-1">
                    {[...(d.historial || [])].reverse().slice(0, 8).map((h, i) => (
                      <div key={i} className="text-xs px-3 py-2 rounded-lg bg-white/5 text-slate-300">
                        <span className="text-slate-500">{(h.fecha || "").slice(0, 10).split("-").reverse().join("/")} · {h.usuario}</span>
                        <div>{h.detalle || h.accion}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Modal>
        );
      })()}

      {modalHistorial && (
        <Modal title={`Historial — ${modalHistorial.empresa_nombre}`} onClose={() => setModalHistorial(null)} wide>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(modalHistorial.historial || []).length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Sin historial</p>
            )}
            {[...(modalHistorial.historial || [])].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/3 border border-white/5">
                <History className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-200">{h.accion?.replace(/_/g, " ")}</span>
                    <span className="text-xs text-slate-500">por {h.usuario}</span>
                  </div>
                  {h.detalle && <p className="text-xs text-slate-400 mt-0.5">{h.detalle}</p>}
                  {h.factura_id && (
                    <button
                      onClick={() => handleVerFactura(h.factura_id)}
                      className="text-xs text-blue-400 hover:text-blue-300 hover:underline mt-1 inline-block"
                    >
                      Ver factura {h.factura_numero || ""} →
                    </button>
                  )}
                  <p className="text-xs text-slate-600 mt-0.5">{new Date(h.fecha).toLocaleString("es-PY")}</p>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* ── Modal: Ver factura vinculada (desde historial) ── */}
      {modalFacturaVista && (
        <Modal title="Factura vinculada" onClose={() => setModalFacturaVista(null)}>
          {modalFacturaVista.loading && (
            <p className="text-sm text-slate-400 text-center py-6">Cargando factura...</p>
          )}
          {modalFacturaVista.error && (
            <p className="text-sm text-red-400 text-center py-6">{modalFacturaVista.error}</p>
          )}
          {modalFacturaVista.factura && (() => {
            const f = modalFacturaVista.factura;
            const totalFac = f.total ?? f.monto_total ?? f.total_a_pagar;
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Número</p>
                    <p className="text-white font-mono font-semibold">{f.numero || f.numero_boleta || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Fecha</p>
                    <p className="text-white">{fmtFecha(f.fecha_emision || f.fecha)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Cliente</p>
                    <p className="text-white">{f.razon_social || f.empresa_nombre || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Estado</p>
                    <p className="text-white capitalize">{f.estado || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
                    <p className="text-white font-semibold">
                      {totalFac !== undefined ? Number(totalFac).toLocaleString("es-PY") : "—"} {f.moneda && f.moneda !== "PYG" ? f.moneda : "Gs."}
                    </p>
                  </div>
                </div>
                {(f.concepto || (f.conceptos || []).length > 0) && (
                  <div className="pt-2 border-t border-white/10">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Concepto</p>
                    {f.concepto && <p className="text-sm text-slate-300">{f.concepto}</p>}
                    {(f.conceptos || []).map((c, i) => (
                      <p key={i} className="text-sm text-slate-300">{c.descripcion} × {c.cantidad}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

// ── Modal genérico ────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-arandu-dark border border-white/10 rounded-2xl shadow-2xl ${wide ? "w-full max-w-2xl" : "w-full max-w-md"}`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
